import camelCase from 'lodash/camelCase';
import { DASHBOARD_KPI_LABELS } from '@/core/constants/dashboard.constants';
import { formatAmount } from '@/core/helpers/number-format';
import { normalizeFitBounds, normalizeLngLat } from '@/core/helpers/dashboard-map';
import { resolveDashboardRoute } from '@/core/helpers/resolve-dashboard-route';
import type {
  DashboardActivityEntity,
  DashboardActivityItem,
  DashboardAlertItem,
  DashboardAlertsData,
  DashboardKpiComparison,
  DashboardKpiItem,
  DashboardMapData,
  DashboardMapItem,
  DashboardMetaData,
  DashboardRecentActivitiesData,
  DashboardRouteTarget,
  DashboardSummaryData,
  DashboardSystemSummaryData,
  InventoryMovementData,
  TopEntitiesData,
  TopEntityItem,
} from '@/core/interfaces/Dashboard';
import type { DashboardWidgetName } from '@/core/interfaces/Dashboard';

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Deep snake_case → camelCase transform for API payloads. */
export function keysToCamelCase<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => keysToCamelCase(item)) as T;
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value)) {
    result[camelCase(key)] = keysToCamelCase(val);
  }
  return result as T;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(value);
}

function normalizeDashboardRoute(
  route: unknown
): DashboardRouteTarget | undefined {
  if (!route || !isPlainObject(route)) {
    return undefined;
  }

  const raw = keysToCamelCase(route) as unknown as DashboardRouteTarget;
  if (!raw.name) {
    return undefined;
  }

  return resolveDashboardRoute(raw) ?? raw;
}

function normalizeMapCenter(
  center: Record<string, unknown> | undefined
): { latitude: number; longitude: number; lat?: number; lng?: number } {
  const normalized = normalizeLngLat(
    Number(center?.longitude ?? center?.lng ?? NaN),
    Number(center?.latitude ?? center?.lat ?? NaN)
  );
  const latitude = normalized?.latitude ?? 16.047079;
  const longitude = normalized?.longitude ?? 108.20623;
  return { latitude, longitude, lat: latitude, lng: longitude };
}

function normalizeMapItem(raw: Record<string, unknown>): DashboardMapItem {
  const normalized = normalizeLngLat(
    Number(raw.longitude ?? raw.lng ?? raw.long ?? NaN),
    Number(raw.latitude ?? raw.lat ?? NaN)
  );

  const warehouseCountRaw =
    raw.warehouseCount ??
    raw.warehouse_count ??
    raw.soKho ??
    raw.so_kho ??
    (Array.isArray(raw.warehouses) ? raw.warehouses.length : undefined);

  const warehouseCount =
    warehouseCountRaw == null || warehouseCountRaw === ''
      ? undefined
      : Number(warehouseCountRaw);

  return {
    id: Number(raw.id),
    entityType: (raw.entityType ??
      raw.entity_type ??
      'unit') as DashboardMapItem['entityType'],
    name: String(raw.name ?? ''),
    shortName: raw.shortName != null ? String(raw.shortName) : undefined,
    provinceCode:
      raw.provinceCode != null
        ? String(raw.provinceCode)
        : raw.province_code != null
          ? String(raw.province_code)
          : null,
    provinceName:
      raw.provinceName != null
        ? String(raw.provinceName)
        : raw.province_name != null
          ? String(raw.province_name)
          : null,
    districtName:
      raw.districtName != null
        ? String(raw.districtName)
        : raw.district_name != null
          ? String(raw.district_name)
          : undefined,
    address: raw.address != null ? String(raw.address) : undefined,
    latitude: normalized?.latitude ?? Number(raw.latitude ?? raw.lat ?? 0),
    longitude: normalized?.longitude ?? Number(raw.longitude ?? raw.lng ?? 0),
    status: (raw.status ?? 'healthy') as DashboardMapItem['status'],
    statusReasons: Array.isArray(raw.statusReasons)
      ? (raw.statusReasons as string[])
      : Array.isArray(raw.status_reasons)
        ? (raw.status_reasons as string[])
        : [],
    warehouseCount:
      warehouseCount != null && Number.isFinite(warehouseCount)
        ? warehouseCount
        : undefined,
    materialCount: Number(raw.materialCount ?? raw.material_count ?? 0),
    inventoryAmount: Number(raw.inventoryAmount ?? raw.inventory_amount ?? 0),
    formattedInventoryAmount:
      raw.formattedInventoryAmount != null
        ? String(raw.formattedInventoryAmount)
        : raw.formatted_inventory_amount != null
          ? String(raw.formatted_inventory_amount)
          : undefined,
    pendingCount: Number(raw.pendingCount ?? raw.pending_count ?? 0),
    alertCount: Number(raw.alertCount ?? raw.alert_count ?? 0),
    errorCount:
      raw.errorCount != null
        ? Number(raw.errorCount)
        : raw.error_count != null
          ? Number(raw.error_count)
          : undefined,
    lastUpdatedAt:
      raw.lastUpdatedAt != null
        ? String(raw.lastUpdatedAt)
        : raw.last_updated_at != null
          ? String(raw.last_updated_at)
          : undefined,
  };
}

function legacySummaryToItems(
  raw: Record<string, unknown>
): DashboardKpiItem[] {
  const items: DashboardKpiItem[] = [];
  const endingAmount = Number(raw.endingAmount ?? raw.ending_amount ?? 0);
  const endingQuantity = Number(raw.endingQuantity ?? raw.ending_quantity ?? 0);
  const materialCount = Number(raw.materialCount ?? raw.material_count ?? 0);
  const warehouseCount = Number(raw.warehouseCount ?? raw.warehouse_count ?? 0);
  const importAmount = Number(raw.importAmount ?? raw.import_amount ?? 0);
  const exportAmount = Number(raw.exportAmount ?? raw.export_amount ?? 0);
  const pendingTicketCount = Number(
    raw.pendingTicketCount ?? raw.pending_ticket_count ?? 0
  );
  const alertCount = Number(raw.alertCount ?? raw.alert_count ?? 0);
  const negativeStockCount = Number(
    raw.negativeStockCount ?? raw.negative_stock_count ?? 0
  );

  items.push({
    key: 'ending_amount',
    label: DASHBOARD_KPI_LABELS.ending_amount,
    value: endingAmount,
    formattedValue: formatAmount(endingAmount),
    severity: 'default',
  });
  items.push({
    key: 'ending_quantity',
    label: DASHBOARD_KPI_LABELS.ending_quantity,
    value: endingQuantity,
    formattedValue: formatNumber(endingQuantity),
    severity: 'default',
  });
  items.push({
    key: 'material_count',
    label: DASHBOARD_KPI_LABELS.material_count,
    value: materialCount,
    formattedValue: formatNumber(materialCount),
    severity: 'success',
  });
  items.push({
    key: 'warehouse_count',
    label: DASHBOARD_KPI_LABELS.warehouse_count,
    value: warehouseCount,
    formattedValue: formatNumber(warehouseCount),
    severity: 'default',
  });
  items.push({
    key: 'import_amount',
    label: DASHBOARD_KPI_LABELS.import_amount,
    value: importAmount,
    formattedValue: formatAmount(importAmount),
    severity: 'success',
  });
  items.push({
    key: 'export_amount',
    label: DASHBOARD_KPI_LABELS.export_amount,
    value: exportAmount,
    formattedValue: formatAmount(exportAmount),
    severity: 'warning',
  });
  items.push({
    key: 'pending_ticket_count',
    label: DASHBOARD_KPI_LABELS.pending_ticket_count,
    value: pendingTicketCount,
    formattedValue: formatNumber(pendingTicketCount),
    severity: pendingTicketCount > 0 ? 'warning' : 'default',
  });
  items.push({
    key: 'alert_count',
    label: DASHBOARD_KPI_LABELS.alert_count,
    value: alertCount,
    formattedValue: formatNumber(alertCount),
    severity: alertCount > 0 ? 'danger' : 'default',
  });
  items.push({
    key: 'negative_stock_count',
    label: DASHBOARD_KPI_LABELS.negative_stock_count,
    value: negativeStockCount,
    formattedValue: formatNumber(negativeStockCount),
    severity: negativeStockCount > 0 ? 'danger' : 'success',
  });

  return items;
}

function normalizeSummaryData(raw: Record<string, unknown>): DashboardSummaryData {
  if (Array.isArray(raw.items) && raw.items.length > 0) {
    const items = (raw.items as Record<string, unknown>[]).map((item) => ({
      key: String(item.key ?? ''),
      label: String(item.label ?? ''),
      value: Number(item.value ?? 0),
      formattedValue: String(
        item.formattedValue ?? item.formatted_value ?? item.value ?? ''
      ),
      unit: item.unit != null ? String(item.unit) : undefined,
      comparison: item.comparison
        ? (keysToCamelCase(item.comparison) as DashboardKpiComparison)
        : undefined,
      severity: (item.severity ?? 'default') as DashboardKpiItem['severity'],
      sparkline: Array.isArray(item.sparkline)
        ? (item.sparkline as number[])
        : undefined,
      route: normalizeDashboardRoute(item.route),
    }));
    return { items };
  }

  return { items: legacySummaryToItems(raw) };
}

function normalizeMovementData(
  raw: Record<string, unknown>
): InventoryMovementData {
  const items = Array.isArray(raw.items)
    ? (raw.items as Record<string, unknown>[]).map((item) => ({
        period: String(item.period ?? ''),
        label: String(item.label ?? item.period ?? ''),
        importValue: Number(item.importValue ?? item.import_value ?? 0),
        exportValue: Number(item.exportValue ?? item.export_value ?? 0),
        endingValue: Number(item.endingValue ?? item.ending_value ?? 0),
      }))
    : [];

  return {
    metric: (raw.metric ?? 'amount') as InventoryMovementData['metric'],
    groupBy: (raw.groupBy ?? raw.group_by ?? 'day') as InventoryMovementData['groupBy'],
    currency: raw.currency != null ? String(raw.currency) : undefined,
    items,
  };
}

function normalizeTopEntitiesData(
  raw: Record<string, unknown>
): TopEntitiesData {
  const items: TopEntityItem[] = Array.isArray(raw.items)
    ? (raw.items as Record<string, unknown>[]).map((item, index) => ({
        rank: Number(item.rank ?? index + 1),
        id: Number(item.id),
        name: String(item.name ?? ''),
        value: Number(
          item.value ?? item.endingAmount ?? item.ending_amount ?? 0
        ),
        formattedValue: String(
          item.formattedValue ??
            item.formatted_value ??
            formatAmount(
              Number(item.endingAmount ?? item.ending_amount ?? item.value ?? 0)
            )
        ),
        status: item.status as TopEntityItem['status'],
        code: item.code != null ? String(item.code) : undefined,
      }))
  : [];

  return {
    entityType: (raw.entityType ??
      raw.entity_type ??
      'material') as TopEntitiesData['entityType'],
    metric: (raw.metric ?? 'ending_amount') as TopEntitiesData['metric'],
    items,
  };
}

function normalizeAlertsData(raw: Record<string, unknown>): DashboardAlertsData {
  const totals = keysToCamelCase(
    (raw.totals as Record<string, unknown>) ?? {
      critical: 0,
      warning: 0,
      info: 0,
    }
  ) as DashboardAlertsData['totals'];

  const items: DashboardAlertItem[] = Array.isArray(raw.items)
    ? (raw.items as Record<string, unknown>[]).map((item) => ({
        key: String(item.key ?? item.type ?? ''),
        severity: (item.severity ?? 'info') as DashboardAlertItem['severity'],
        count: Number(item.count ?? 0),
        label: String(item.label ?? ''),
        description:
          item.description != null ? String(item.description) : undefined,
        route: normalizeDashboardRoute(item.route),
      }))
    : [];

  return { totals, items };
}

function normalizeActivitiesData(
  raw: Record<string, unknown>
): DashboardRecentActivitiesData {
  const items: DashboardActivityItem[] = Array.isArray(raw.items)
    ? (raw.items as Record<string, unknown>[]).map((item) => {
        const occurredAt = String(
          item.occurredAt ?? item.occurred_at ?? item.createdAt ?? item.created_at ?? ''
        );
        const actorRaw = item.actor;
        let actor: DashboardActivityItem['actor'];
        if (typeof actorRaw === 'string') {
          actor = actorRaw;
        } else if (isPlainObject(actorRaw)) {
          actor = {
            id: Number(actorRaw.id ?? 0),
            name: String(actorRaw.name ?? ''),
          };
        }

        return {
          id: Number(item.id),
          occurredAt,
          timeLabel: String(
            item.timeLabel ??
              item.time_label ??
              (occurredAt
                ? new Date(occurredAt).toLocaleTimeString('vi-VN', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '')
          ),
          type: String(item.type ?? ''),
          severity: item.severity as DashboardActivityItem['severity'],
          actor,
          entity: item.entity
            ? (keysToCamelCase(item.entity) as DashboardActivityEntity)
            : undefined,
          message: String(item.message ?? item.label ?? ''),
          route: normalizeDashboardRoute(item.route),
        };
      })
    : [];

  return {
    items,
    nextCursor:
      raw.nextCursor != null
        ? String(raw.nextCursor)
        : raw.next_cursor != null
          ? String(raw.next_cursor)
          : null,
  };
}

function normalizeMapData(raw: Record<string, unknown>): DashboardMapData {
  const sourceItems = Array.isArray(raw.items)
    ? raw.items
    : Array.isArray(raw.markers)
      ? raw.markers
      : [];

  const items = (sourceItems as Record<string, unknown>[]).map(normalizeMapItem);
  const focusRaw = (raw.focus as Record<string, unknown>) ?? {};
  const center = normalizeMapCenter(
    (focusRaw.center as Record<string, unknown>) ?? undefined
  );
  const boundsRaw = focusRaw.bounds as
    | [[number, number], [number, number]]
    | null
    | undefined;

  return {
    mode: (raw.mode ?? 'units') as DashboardMapData['mode'],
    countryCode:
      raw.countryCode != null
        ? String(raw.countryCode)
        : raw.country_code != null
          ? String(raw.country_code)
          : undefined,
    focus: {
      type: (focusRaw.type ?? 'national') as DashboardMapData['focus']['type'],
      center,
      zoom: Number(focusRaw.zoom ?? 5),
      bounds: normalizeFitBounds(boundsRaw),
      provinceCode:
        focusRaw.provinceCode != null
          ? String(focusRaw.provinceCode)
          : focusRaw.province_code != null
            ? String(focusRaw.province_code)
            : null,
    },
    items,
    markers: items,
    unlocatedCount: Number(raw.unlocatedCount ?? raw.unlocated_count ?? 0),
    unlocatedItems: Array.isArray(raw.unlocatedItems)
      ? keysToCamelCase(raw.unlocatedItems)
      : Array.isArray(raw.unlocated_items)
        ? keysToCamelCase(raw.unlocated_items)
        : undefined,
  };
}

function normalizeMetaData(raw: Record<string, unknown>): DashboardMetaData {
  const camel = keysToCamelCase(raw) as Record<string, unknown>;
  const permissionsRaw = (camel.permissions as Record<string, unknown>) ?? {};

  return {
    capDonVi: Number(camel.capDonVi ?? camel.cap_don_vi ?? 1),
    defaultScope: keysToCamelCase(
      camel.defaultScope ?? camel.default_scope ?? { type: 'national' }
    ) as DashboardMetaData['defaultScope'],
    map: {
      countryCode: String(
        (camel.map as Record<string, unknown>)?.countryCode ??
          (camel.map as Record<string, unknown>)?.country_code ??
          'VN'
      ),
      center: normalizeMapCenter(
        ((camel.map as Record<string, unknown>)?.center as Record<string, unknown>) ??
          undefined
      ),
      zoom: Number((camel.map as Record<string, unknown>)?.zoom ?? 5),
      minZoom: Number(
        (camel.map as Record<string, unknown>)?.minZoom ??
          (camel.map as Record<string, unknown>)?.min_zoom ??
          4.5
      ),
      maxZoom: Number(
        (camel.map as Record<string, unknown>)?.maxZoom ??
          (camel.map as Record<string, unknown>)?.max_zoom ??
          16
      ),
    },
    permissions: {
      canViewAllUnits: Boolean(
        permissionsRaw.canViewAllUnits ?? permissionsRaw.can_view_all_units
      ),
      canFilterUnit: Boolean(
        permissionsRaw.canFilterUnit ??
          permissionsRaw.can_filter_unit ??
          permissionsRaw.canFilterUnits ??
          permissionsRaw.can_filter_units
      ),
      canFilterWarehouse: Boolean(
        permissionsRaw.canFilterWarehouse ??
          permissionsRaw.can_filter_warehouse ??
          permissionsRaw.canViewWarehouses ??
          permissionsRaw.can_view_warehouses
      ),
      canViewFinancialValues: Boolean(
        permissionsRaw.canViewFinancialValues ??
          permissionsRaw.can_view_financial_values
      ),
      canViewAlerts: Boolean(
        permissionsRaw.canViewAlerts ?? permissionsRaw.can_view_alerts ?? true
      ),
    },
    versions: keysToCamelCase(camel.versions ?? {}) as DashboardMetaData['versions'],
  };
}

function normalizeSystemSummaryData(
  raw: Record<string, unknown>
): DashboardSystemSummaryData {
  const items = Array.isArray(raw.items)
    ? (raw.items as Record<string, unknown>[]).map((item) => ({
        key: String(item.key ?? ''),
        label: String(item.label ?? ''),
        value: Number(item.value ?? 0),
        formattedValue: String(
          item.formattedValue ?? item.formatted_value ?? item.value ?? ''
        ),
        severity: (item.severity ?? 'info') as DashboardSystemSummaryData['items'][0]['severity'],
      }))
    : [];

  return { items };
}

export function adaptDashboardWidgetData<T>(
  widget: DashboardWidgetName,
  data: unknown
): T {
  if (data == null) {
    return data as T;
  }

  const raw = keysToCamelCase(data) as Record<string, unknown>;

  switch (widget) {
    case 'meta':
      return normalizeMetaData(raw) as T;
    case 'summary':
      return normalizeSummaryData(raw) as T;
    case 'system-summary':
      return normalizeSystemSummaryData(raw) as T;
    case 'map':
      return normalizeMapData(raw) as T;
    case 'inventory-movement':
      return normalizeMovementData(raw) as T;
    case 'top-entities':
      return normalizeTopEntitiesData(raw) as T;
    case 'alerts':
      return normalizeAlertsData(raw) as T;
    case 'recent-activities':
      return normalizeActivitiesData(raw) as T;
  }

  return raw as T;
}
